import type { AssessmentRule, AssessmentSection, Question } from '@app/src/types/resourceExplorer'
import { getScoringOptionValues } from '@app/src/utils/assessmentRules'
import type { ImportantKpiEntry } from './types'
import { SelectionStatus } from './types'

export const isExpandableQuestion = (question: Question, rules: AssessmentRule[]): boolean =>
  getScoringOptionValues(question, rules).length > 1

export const isOptionStarred = (entries: ImportantKpiEntry[], questionId: number, optionValue: string): boolean => {
  const entry = entries.find(e => e.questionId === questionId)
  return Boolean(entry?.importantOptions?.includes(optionValue))
}

export const isQuestionStarred = (
  entries: ImportantKpiEntry[],
  question: Question,
  rules: AssessmentRule[],
): SelectionStatus => {
  const entry = entries.find(e => e.questionId === question.id)
  if (!entry) return SelectionStatus.None
  if (!isExpandableQuestion(question, rules)) return SelectionStatus.All
  const scoringOptions = getScoringOptionValues(question, rules)
  const selected = entry.importantOptions ?? []
  if (selected.length === 0) return SelectionStatus.None
  return selected.length === scoringOptions.length ? SelectionStatus.All : SelectionStatus.Some
}

const sectionQuestions = (section: AssessmentSection) => section.questions ?? []

export const isSectionStarred = (
  entries: ImportantKpiEntry[],
  section: AssessmentSection,
  rules: AssessmentRule[],
): SelectionStatus => {
  const questions = sectionQuestions(section)
  if (questions.length === 0) return SelectionStatus.None
  const statuses = questions.map(aq => isQuestionStarred(entries, aq.question, rules))
  if (statuses.every(s => s === SelectionStatus.All)) return SelectionStatus.All
  if (statuses.every(s => s === SelectionStatus.None)) return SelectionStatus.None
  return SelectionStatus.Some
}

export const countSelectedKpis = (entries: ImportantKpiEntry[]): number =>
  entries.reduce((sum, entry) => sum + (entry.importantOptions?.length ?? 1), 0)

export const toggleOption = (
  entries: ImportantKpiEntry[],
  question: Question,
  optionValue: string,
): ImportantKpiEntry[] => {
  const existing = entries.find(e => e.questionId === question.id)
  if (!existing) {
    return [...entries, { questionId: question.id, importantOptions: [optionValue] }]
  }
  const currentOptions = existing.importantOptions ?? []
  const nextOptions = currentOptions.includes(optionValue)
    ? currentOptions.filter(v => v !== optionValue)
    : [...currentOptions, optionValue]
  if (nextOptions.length === 0) {
    return entries.filter(e => e.questionId !== question.id)
  }
  return entries.map(e => (e.questionId === question.id ? { ...e, importantOptions: nextOptions } : e))
}

const buildEntryForQuestion = (question: Question, rules: AssessmentRule[]): ImportantKpiEntry => {
  const scoringOptions = getScoringOptionValues(question, rules)
  return scoringOptions.length > 1
    ? { questionId: question.id, importantOptions: scoringOptions }
    : { questionId: question.id }
}

export const toggleQuestion = (
  entries: ImportantKpiEntry[],
  question: Question,
  rules: AssessmentRule[],
): ImportantKpiEntry[] => {
  const status = isQuestionStarred(entries, question, rules)
  if (status === SelectionStatus.All) {
    return entries.filter(e => e.questionId !== question.id)
  }
  const without = entries.filter(e => e.questionId !== question.id)
  return [...without, buildEntryForQuestion(question, rules)]
}

export const toggleSection = (
  entries: ImportantKpiEntry[],
  section: AssessmentSection,
  rules: AssessmentRule[],
): ImportantKpiEntry[] => {
  const questions = sectionQuestions(section)
  const sectionQuestionIds = new Set(questions.map(aq => aq.questionId))
  const status = isSectionStarred(entries, section, rules)
  if (status === SelectionStatus.All) {
    return entries.filter(e => !sectionQuestionIds.has(e.questionId))
  }
  const without = entries.filter(e => !sectionQuestionIds.has(e.questionId))
  const additions = questions.map(aq => buildEntryForQuestion(aq.question, rules))
  return [...without, ...additions]
}
